import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_SERVER } from "config/constant";

// Chakra imports
import {
  Flex,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  Td,
  Badge,
  useColorModeValue,
  Box,
  Heading,
  Button,
} from "@chakra-ui/react";

import { NavLink } from "react-router-dom";

// Custom components
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

import axios from "axios";

function PickingPanel() {
  const [order, setOrder] = useState();
  const [picking, setPicking] = useState([]);
  const [scanStatus, setScanStatus] = useState(
    "🔍 Scanner activo - Escanea productos automáticamente"
  );
  const [lastScanResult, setLastScanResult] = useState("");
  let scanCount = 0;

  const { code: reference } = useParams();
  const textColor = useColorModeValue("gray.700", "white");

  useEffect(() => {
    getOrderDetails();
    updatePickingTable();
    setupKeyboardScanner();
    return () => {
      cleanupKeyboardScanner();
    };
  }, []);

  const setupKeyboardScanner = () => {
    // Listener para lector de códigos de barras por teclado
    let barcodeBuffer = "";
    let barcodeTimeout = null;

    const handleKeyDown = (event) => {
      // Evitar procesar cuando está en campos de input
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA"
      ) {
        return;
      }

      clearTimeout(barcodeTimeout);

      if (event.key === "Enter") {
        if (barcodeBuffer.length > 5) {
          processScannedCode(barcodeBuffer.trim());
        }
        barcodeBuffer = "";
      } else if (event.key.length === 1) {
        barcodeBuffer += event.key;
      }

      // Limpiar buffer si no hay actividad por 2 segundos
      barcodeTimeout = setTimeout(() => {
        barcodeBuffer = "";
      }, 2000);
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      clearTimeout(barcodeTimeout);
    };
  };

  const cleanupKeyboardScanner = () => {
    // Cleanup handled in setupKeyboardScanner
  };

  const processScannedCode = async (code) => {
    try {
      setScanStatus(`⏳ Procesando código: ${code}`);

      // Buscar producto en la orden actual
      const response = await axios.get(
        `${API_SERVER}orders/check_product/${reference}/${code}`
      );

      if (response.data.success && response.data.product) {
        const product = response.data.product;

        // Validar que haya stock disponible
        if (product.quantity > 0) {
          // Hacer picking automático de 1 unidad
          await performAutoPicking(product);
        } else {
          setScanStatus(`❌ Sin stock para ${code}`);
          setTimeout(
            () =>
              setScanStatus(
                "🔍 Scanner activo - Escanea productos automáticamente"
              ),
            2000
          );
        }
      } else {
        setScanStatus(`❌ Producto no encontrado: ${code}`);
        setTimeout(
          () =>
            setScanStatus(
              "🔍 Scanner activo - Escanea productos automáticamente"
            ),
          2000
        );
      }
    } catch (error) {
      console.error("Error processing scan:", error);
      setScanStatus(`❌ Error procesando ${code}`);
      setTimeout(
        () =>
          setScanStatus(
            "🔍 Scanner activo - Escanea productos automáticamente"
          ),
        2000
      );
    }
  };

  const performAutoPicking = async (product) => {
    try {
      setScanStatus(`💾 Guardando picking para ${product.code}...`);

      const formData = new FormData();
      formData.append("order_id", reference);
      formData.append("product_id", product.id);
      formData.append("quantity", 1); // Siempre 1 unidad por escaneo
      formData.append("total", product.quantity);

      const response = await axios.post(
        `${API_SERVER}picking/save_picking`,
        formData
      );

      if (response.data.success) {
        scanCount++;
        setScanStatus(`✅ Picking guardado! Total escaneos: ${scanCount}`);
        setLastScanResult(`${product.code} - ${product.title}`);

        // Actualizar datos
        await updatePickingTable();

        setTimeout(
          () =>
            setScanStatus(
              "🔍 Scanner activo - Escanea productos automáticamente"
            ),
          2000
        );
      } else {
        setScanStatus(`❌ Error al guardar picking`);
        setTimeout(
          () =>
            setScanStatus(
              "🔍 Scanner activo - Escanea productos automáticamente"
            ),
          2000
        );
      }
    } catch (error) {
      console.error("Error saving picking:", error);
      setScanStatus(`❌ Error al guardar picking`);
      setTimeout(
        () =>
          setScanStatus(
            "🔍 Scanner activo - Escanea productos automáticamente"
          ),
        2000
      );
    }
  };

  const getOrderDetails = async () => {
    try {
      const { data } = await axios.get(
        `${API_SERVER}orders/get_order_details/${reference}`
      );
      if (data.success) {
        setOrder(data.order);
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    }
  };

  const updatePickingTable = async () => {
    try {
      const { data } = await axios.get(
        `${API_SERVER}picking/picking_info/${reference}`
      );
      if (data.success) {
        setPicking(data.picking || []);
      }
    } catch (error) {
      console.error("Error updating picking table:", error);
    }
  };

  return (
    <Flex direction="column" pt={{ base: "60px", md: "50px" }}>
      <Flex style={{ alignItems: "center", justifyContent: "start" }}>
        <NavLink to={`/admin/pedidos`}>
          <Button
            mt="30px"
            bg="blue.400"
            color="white"
            borderRadius="lg"
            transition="all 0.2s ease"
            _hover={{
              bg: "blue.500",
              transform: "translateY(-1px)",
            }}
          >
            ← Volver a Pedidos
          </Button>
        </NavLink>
      </Flex>

      {/* Panel principal simplificado */}
      <Card
        overflowX={{ sm: "scroll", xl: "hidden" }}
        mt="30px"
        bg="white"
        shadow="lg"
        borderRadius="lg"
      >
        <CardHeader p="6px 0px 22px 0px">
          <Flex justify="space-between" align="center">
            <Box>
              <Heading size="lg" color={textColor} mb="2">
                📦 PEDIDO {reference}
              </Heading>
              <Text color="gray.600" fontSize="md">
                {order ? order.reference : reference}
              </Text>
            </Box>
          </Flex>
        </CardHeader>

        <CardBody>
          {/* Estado del scanner */}
          <Box
            p="6"
            bg="linear-gradient(135deg, green.50, blue.50)"
            borderRadius="lg"
            mb="6"
            textAlign="center"
            border="2px solid green.200"
          >
            <Heading size="md" color={textColor} mb="3">
              📡 ESTADO DEL PICKING AUTOMÁTICO
            </Heading>
            <Text fontSize="lg" color={textColor} fontWeight="bold" mb="2">
              {scanStatus}
            </Text>
            <Text fontSize="sm" color="gray.600">
              Escanea cualquier código de barras con tu lector para procesar
              automáticamente
            </Text>
            {lastScanResult && (
              <Text fontSize="md" color="green.600" fontWeight="bold" mt="2">
                Último escaneo: {lastScanResult}
              </Text>
            )}
          </Box>

          {/* Información del pedido */}
          <Box mb="6">
            <Heading size="md" color={textColor} mb="4">
              📋 INFORMACIÓN DEL PEDIDO
            </Heading>
            <Flex gap="4" wrap="wrap">
              <Badge
                bg="blue.100"
                color="blue.800"
                p="8px 16px"
                borderRadius="8px"
                fontSize="md"
              >
                Pedido: {reference}
              </Badge>
              <Badge
                bg="purple.100"
                color="purple.800"
                p="8px 16px"
                borderRadius="8px"
                fontSize="md"
              >
                Escaneos totales: {scanCount}
              </Badge>
            </Flex>
          </Box>
        </CardBody>
      </Card>

      {/* Historial de picking */}
      {picking && picking.length > 0 && (
        <Card
          bg="gray.50"
          mt="30px"
          overflowX={{ sm: "scroll", xl: "hidden" }}
          shadow="lg"
          borderRadius="lg"
        >
          <CardHeader p="6px 0px 22px 0px">
            <Text
              fontSize={{ base: "lg", md: "xl", xl: "2xl" }}
              color={textColor}
              fontWeight="bold"
            >
              📋 HISTORIAL DE PICKING
            </Text>
          </CardHeader>
          <CardBody>
            <Table
              variant="simple"
              color={textColor}
              size={{ base: "sm", md: "md" }}
            >
              <Thead>
                <Tr my=".8rem" pl="0px" color="gray.400">
                  <Th pl="0px" color="gray.400">
                    Código
                  </Th>
                  <Th color="gray.400">Cantidad</Th>
                  <Th color="gray.400">Fecha</Th>
                </Tr>
              </Thead>
              <Tbody>
                {picking &&
                  picking.length > 0 &&
                  picking.map((row, key) => (
                    <Tr key={key}>
                      <Td>
                        <Flex direction="column">
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            color={textColor}
                            fontWeight="bold"
                          >
                            {row.product_code}
                          </Text>
                        </Flex>
                      </Td>
                      <Td>
                        <Badge
                          bg={row.quantity > 0 ? "green.400" : "gray.400"}
                          color="white"
                          fontSize={{ base: "12px", md: "14px" }}
                          p="3px 10px"
                          borderRadius="8px"
                          transition="all 0.2s ease"
                          _hover={{ transform: "scale(1.05)" }}
                        >
                          {row.quantity}
                        </Badge>
                      </Td>
                      <Td>
                        <Flex direction="column">
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            color={textColor}
                            fontWeight="bold"
                          >
                            {row.updated_at}
                          </Text>
                        </Flex>
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      )}
    </Flex>
  );
}

export default PickingPanel;
